import type {
  SpendingGoalWithProgress,
  CreateSpendingGoalDto,
  UpdateSpendingGoalDto,
} from "@repo/share-log-interfaces";
import type { StateStatus, StoreDeps } from "../types";
import type { ImmerSet } from "../helpers/create-store";
import { createStore } from "../helpers/create-store";
import { withFetchStatus } from "../helpers/with-fetch-status";
import { createReset } from "../helpers/create-reset";

const DEFAULT_LIMIT = 20;

interface SpendingGoalState {
  goals: SpendingGoalWithProgress[];
  total: number;
  page: number;
  limit: number;
  hasMore: boolean;
  status: StateStatus;
}

interface SpendingGoalActions {
  fetchGoals: (workspaceId: number) => Promise<void>;
  loadMore: (workspaceId: number) => Promise<void>;
  createGoal: (
    workspaceId: number,
    dto: CreateSpendingGoalDto,
  ) => Promise<SpendingGoalWithProgress>;
  updateGoal: (
    workspaceId: number,
    goalId: number,
    dto: UpdateSpendingGoalDto,
  ) => Promise<SpendingGoalWithProgress>;
  deleteGoal: (workspaceId: number, goalId: number) => Promise<void>;
  toggleActive: (
    workspaceId: number,
    goalId: number,
    isActive: boolean,
  ) => Promise<void>;
  reset: () => void;
}

export type SpendingGoalStore = SpendingGoalState & SpendingGoalActions;

const initialState: SpendingGoalState = {
  goals: [],
  total: 0,
  page: 1,
  limit: DEFAULT_LIMIT,
  hasMore: false,
  status: "idle",
};

export function createSpendingGoalStore(deps: StoreDeps) {
  return createStore<SpendingGoalStore>("SpendingGoalStore", (set, get) => ({
    ...initialState,

    fetchGoals: (workspaceId) =>
      withFetchStatus(
        (s) => set((state) => { state.status = s; }),
        deps.logger,
        async () => {
          const result = await deps.api.spendingGoal.list(workspaceId, {
            page: 1,
            limit: DEFAULT_LIMIT,
          });
          set((state) => {
            state.goals = result.goals;
            state.total = result.total;
            state.page = result.page;
            state.limit = result.limit;
            state.hasMore = result.hasMore;
          });
        },
      ),

    loadMore: async (workspaceId) => {
      const { hasMore, status, page } = get();
      if (!hasMore || status === "loading") return;

      await withFetchStatus(
        (s) => set((state) => { state.status = s; }),
        deps.logger,
        async () => {
          const nextPage = page + 1;
          const result = await deps.api.spendingGoal.list(workspaceId, {
            page: nextPage,
            limit: DEFAULT_LIMIT,
          });
          set((state) => {
            state.goals = [...state.goals, ...result.goals];
            state.total = result.total;
            state.page = result.page;
            state.limit = result.limit;
            state.hasMore = result.hasMore;
          });
        },
      );
    },

    createGoal: async (workspaceId, dto) => {
      const goal = await deps.api.spendingGoal.create(workspaceId, dto);
      set((state) => {
        state.goals.unshift(goal);
        state.total += 1;
      });
      return goal;
    },

    updateGoal: async (workspaceId, goalId, dto) => {
      const goal = await deps.api.spendingGoal.update(
        workspaceId,
        goalId,
        dto,
      );
      set((state) => {
        const idx = state.goals.findIndex((g) => g.id === goalId);
        if (idx !== -1) state.goals[idx] = goal;
      });
      return goal;
    },

    deleteGoal: async (workspaceId, goalId) => {
      await deps.api.spendingGoal.remove(workspaceId, goalId);
      set((state) => {
        state.goals = state.goals.filter((g) => g.id !== goalId);
        state.total -= 1;
      });
    },

    toggleActive: async (workspaceId, goalId, isActive) => {
      const goal = await deps.api.spendingGoal.update(workspaceId, goalId, {
        isActive,
      });
      set((state) => {
        const idx = state.goals.findIndex((g) => g.id === goalId);
        if (idx !== -1) state.goals[idx] = goal;
      });
    },

    reset: createReset(set as ImmerSet<SpendingGoalState>, initialState),
  }));
}
